// src/services/AnalyticsService.js
import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

class AnalyticsService {

  // Load all public_service podcast IDs — these are excluded from payment calc.
  // Result is cached for the lifetime of the service instance to avoid
  // repeated Firestore reads within a single analytics session.
  async _getPublicServicePodcastIds() {
    if (this._publicServiceIds) return this._publicServiceIds;
    const snap = await getDocs(
      query(collection(db, 'podcasts'), where('contentType', '==', 'public_service'))
    );
    this._publicServiceIds = new Set(snap.docs.map(d => d.id));
    return this._publicServiceIds;
  }

  // Filter a podcastTimes map to only creator (compensated) entries.
  _creatorOnly(podcastTimes, publicServiceIds) {
    const filtered = {};
    for (const [id, time] of Object.entries(podcastTimes)) {
      if (!publicServiceIds.has(id)) filtered[id] = time;
    }
    return filtered;
  }

  // Get listening stats for a specific creator's podcasts
  async getCreatorAnalytics(creatorId) {
    try {
      // Get all monthly summaries across all users
      const monthlyQuery = collectionGroup(db, 'monthly_summaries');
      const monthlySnapshot = await getDocs(monthlyQuery);

      // Public-service podcasts are excluded from the payment denominator
      const publicServiceIds = await this._getPublicServicePodcastIds();

      // Get creator's podcasts to filter data
      const podcastsQuery = query(
        collection(db, 'podcasts'),
        where('creatorId', '==', creatorId)
      );
      const podcastsSnapshot = await getDocs(podcastsQuery);
      const creatorPodcastIds = podcastsSnapshot.docs.map(doc => doc.id);
      
      if (creatorPodcastIds.length === 0) {
        return this.getEmptyAnalytics();
      }
      
      let totalListeningTime = 0;
      let totalEarnings = 0;
      let monthlyListeningTime = 0;
      let uniqueListeners = new Set();
      const podcastStats = {};
      
      // Process all monthly summaries
      monthlySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const podcastTimes = data.podcastTimes || {};
        
        // Check if this user listened to any of creator's podcasts
        let userListenedToCreator = false;
        
        for (const [podcastId, listeningTime] of Object.entries(podcastTimes)) {
          if (creatorPodcastIds.includes(podcastId)) {
            userListenedToCreator = true;
            totalListeningTime += listeningTime;
            
            // Track per-podcast stats
            if (!podcastStats[podcastId]) {
              podcastStats[podcastId] = { totalTime: 0, listeners: new Set() };
            }
            podcastStats[podcastId].totalTime += listeningTime;
            podcastStats[podcastId].listeners.add(data.userId);
            
            // Earnings: ratio is over creator-only time so public-service
            // listening doesn't dilute the 80% pool.
            const creatorOnlyTimes = this._creatorOnly(podcastTimes, publicServiceIds);
            const userCreatorTotalTime = Object.values(creatorOnlyTimes).reduce((s, t) => s + t, 0);
            if (userCreatorTotalTime > 0) {
              const userCreatorRatio = listeningTime / userCreatorTotalTime;
              const userCreatorEarnings = 30 * 0.8 * userCreatorRatio;
              totalEarnings += userCreatorEarnings;
            }
          }
        }
        
        if (userListenedToCreator) {
          uniqueListeners.add(data.userId);
        }
        
        // Check if this is current month
        const currentMonth = this.getCurrentMonth();
        if (data.month === currentMonth) {
          for (const [podcastId, listeningTime] of Object.entries(podcastTimes)) {
            if (creatorPodcastIds.includes(podcastId)) {
              monthlyListeningTime += listeningTime;
            }
          }
        }
      });
      
      return {
        totalListeningTime: Math.round(totalListeningTime),
        totalListeningHours: Math.round(totalListeningTime / 3600),
        monthlyListeningTime: Math.round(monthlyListeningTime),
        monthlyListeningHours: Math.round(monthlyListeningTime / 3600),
        totalEarnings: Math.round(totalEarnings * 100) / 100, // Round to 2 decimals
        monthlyEarnings: await this.calculateMonthlyEarnings(creatorPodcastIds),
        totalListeners: uniqueListeners.size,
        podcastStats: this.formatPodcastStats(podcastStats),
        lastUpdated: new Date()
      };
      
    } catch (error) {
      console.error('Error fetching creator analytics:', error);
      return this.getEmptyAnalytics();
    }
  }
  
  // Get analytics for a specific podcast
  async getPodcastAnalytics(podcastId) {
    try {
      const monthlyQuery = collectionGroup(db, 'monthly_summaries');
      const monthlySnapshot = await getDocs(monthlyQuery);
      const publicServiceIds = await this._getPublicServicePodcastIds();

      let totalListeningTime = 0;
      let monthlyListeningTime = 0;
      let totalEarnings = 0;
      const listeners = new Set();
      const currentMonth = this.getCurrentMonth();

      monthlySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const podcastTimes = data.podcastTimes || {};

        if (podcastTimes[podcastId]) {
          const listeningTime = podcastTimes[podcastId];
          totalListeningTime += listeningTime;
          listeners.add(data.userId);

          // Earnings: denominator is creator-only listening time.
          const creatorOnlyTimes = this._creatorOnly(podcastTimes, publicServiceIds);
          const userCreatorTotalTime = Object.values(creatorOnlyTimes).reduce((s, t) => s + t, 0);
          if (userCreatorTotalTime > 0) {
            const podcastRatio = listeningTime / userCreatorTotalTime;
            const podcastEarnings = 30 * 0.8 * podcastRatio;
            totalEarnings += podcastEarnings;
          }

          // Monthly stats
          if (data.month === currentMonth) {
            monthlyListeningTime += listeningTime;
          }
        }
      });
      
      return {
        podcastId,
        totalListeningTime: Math.round(totalListeningTime),
        totalListeningHours: Math.round(totalListeningTime / 3600),
        monthlyListeningTime: Math.round(monthlyListeningTime),
        monthlyListeningHours: Math.round(monthlyListeningTime / 3600),
        totalEarnings: Math.round(totalEarnings * 100) / 100,
        totalListeners: listeners.size,
        averageListeningTime: listeners.size > 0 ? Math.round(totalListeningTime / listeners.size) : 0
      };
      
    } catch (error) {
      console.error('Error fetching podcast analytics:', error);
      return {
        podcastId,
        totalListeningTime: 0,
        totalListeningHours: 0,
        monthlyListeningTime: 0,
        monthlyListeningHours: 0,
        totalEarnings: 0,
        totalListeners: 0,
        averageListeningTime: 0
      };
    }
  }
  
  // Get recent listening sessions for a creator
  async getRecentListeningSessions(creatorId, limit = 10) {
    try {
      // Get creator's podcasts
      const podcastsQuery = query(
        collection(db, 'podcasts'),
        where('creatorId', '==', creatorId)
      );
      const podcastsSnapshot = await getDocs(podcastsQuery);
      const creatorPodcastIds = podcastsSnapshot.docs.map(doc => doc.id);
      
      if (creatorPodcastIds.length === 0) return [];
      
      // Get recent listening sessions across all users
      const sessionsQuery = collectionGroup(db, 'listening_history');
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      const recentSessions = [];
      
      sessionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (creatorPodcastIds.includes(data.podcastId)) {
          recentSessions.push({
            id: doc.id,
            ...data,
            startTime: data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime)
          });
        }
      });
      
      // Sort by most recent and limit
      return recentSessions
        .sort((a, b) => b.startTime - a.startTime)
        .slice(0, limit);
        
    } catch (error) {
      console.error('Error fetching recent sessions:', error);
      return [];
    }
  }
  
  // Calculate monthly earnings for creator
  async calculateMonthlyEarnings(creatorPodcastIds) {
    try {
      const currentMonth = this.getCurrentMonth();
      const publicServiceIds = await this._getPublicServicePodcastIds();
      const monthlyQuery = query(
        collectionGroup(db, 'monthly_summaries'),
        where('month', '==', currentMonth)
      );
      const monthlySnapshot = await getDocs(monthlyQuery);

      let monthlyEarnings = 0;

      monthlySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const podcastTimes = data.podcastTimes || {};

        // Denominator: creator-only listening time (public-service excluded)
        const creatorOnlyTimes = this._creatorOnly(podcastTimes, publicServiceIds);
        const userCreatorTotalTime = Object.values(creatorOnlyTimes).reduce((s, t) => s + t, 0);

        let creatorListeningTime = 0;
        for (const [podcastId, listeningTime] of Object.entries(podcastTimes)) {
          if (creatorPodcastIds.includes(podcastId)) {
            creatorListeningTime += listeningTime;
          }
        }

        if (userCreatorTotalTime > 0 && creatorListeningTime > 0) {
          const creatorRatio = creatorListeningTime / userCreatorTotalTime;
          const userCreatorEarnings = 30 * 0.8 * creatorRatio;
          monthlyEarnings += userCreatorEarnings;
        }
      });

      return Math.round(monthlyEarnings * 100) / 100;
    } catch (error) {
      console.error('Error calculating monthly earnings:', error);
      return 0;
    }
  }
  
  // Helper methods
  getCurrentMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
  
  formatPodcastStats(podcastStats) {
    const formatted = {};
    for (const [podcastId, stats] of Object.entries(podcastStats)) {
      formatted[podcastId] = {
        totalTime: Math.round(stats.totalTime),
        totalHours: Math.round(stats.totalTime / 3600),
        listeners: stats.listeners.size,
        averageTime: stats.listeners.size > 0 ? Math.round(stats.totalTime / stats.listeners.size) : 0
      };
    }
    return formatted;
  }
  
  getEmptyAnalytics() {
    return {
      totalListeningTime: 0,
      totalListeningHours: 0,
      monthlyListeningTime: 0,
      monthlyListeningHours: 0,
      totalEarnings: 0,
      monthlyEarnings: 0,
      totalListeners: 0,
      podcastStats: {},
      lastUpdated: new Date()
    };
  }
  
  // Format time duration for display
  formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
  
  // Format currency for display
  formatCurrency(amount, currency = 'DKK') {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  }
}

const analyticsServiceInstance = new AnalyticsService();
export default analyticsServiceInstance;